import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, PawPrint, Images } from "lucide-react";
import { Link } from "react-router-dom";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      if (data.length) setSelected(data[0]);
    }).catch(() => toast.error("Failed to load clients")).finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <header>
        <div className="label-accent">CRM</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Clients & pets</h1>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-[#E5DCCE] p-4 shadow-sm">
          {loading ? <p className="text-sm text-[#6B625B] p-3">Loading…</p> :
            clients.length === 0 ? <p className="text-sm text-[#6B625B] p-3">No clients yet.</p> :
              <ul className="space-y-1" data-testid="client-list">
                {clients.map(c => (
                  <li key={c.id}>
                    <button onClick={() => setSelected(c)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
                        selected?.id === c.id ? "bg-[#F5E6CF]" : "hover:bg-[#F3EDE4]"
                      }`}
                      data-testid={`client-${c.id}`}>
                      <div className="font-semibold text-sm">{c.name}</div>
                      <div className="text-xs text-[#6B625B] truncate">{c.email}</div>
                      <div className="text-[10px] text-[#6B625B] mt-1 flex items-center gap-1">
                        <PawPrint className="w-3 h-3"/> {c.pets?.length || 0} pet{(c.pets?.length||0)===1?"":"s"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>}
        </div>

        <div className="lg:col-span-3">
          {selected ? (
            <div className="bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm space-y-5">
              <div>
                <h2 className="font-display text-2xl font-bold">{selected.name}</h2>
                <div className="mt-2 text-sm text-[#6B625B] space-y-1">
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4"/> {selected.email}</div>
                  {selected.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4"/> {selected.phone}</div>}
                  {selected.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4"/> {selected.address}</div>}
                </div>
              </div>

              <div>
                <h3 className="font-display text-lg font-bold mb-3">Pets</h3>
                {(!selected.pets || selected.pets.length === 0) ? (
                  <p className="text-sm text-[#6B625B]">No pets on file.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {selected.pets.map(p => (
                      <Link key={p.id} to={`/pets/${p.id}/gallery`} data-testid={`gpet-card-${p.id}`}>
                        <div className="rounded-2xl border border-[#E5DCCE] p-4 bg-[#FAF7F2]/60 hover:border-[#C85A17] transition-colors">
                          <div className="flex items-center gap-3">
                            {p.photo_url ? (
                              <img src={p.photo_url} alt={p.name} className="w-12 h-12 rounded-xl object-cover"/>
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-[#F5E6CF] grid place-content-center text-[#A9480F]">
                                <PawPrint className="w-5 h-5"/>
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="font-semibold flex items-center gap-1.5">
                                {p.name}
                                <Images className="w-3 h-3 text-[#C85A17]"/>
                              </div>
                              <div className="text-xs text-[#6B625B]">{p.breed} · {p.weight_lbs} lbs</div>
                            </div>
                          </div>
                          {p.temperament && <div className="text-xs mt-3 text-[#6B625B]"><span className="font-semibold text-[#1F1A17]">Temperament:</span> {p.temperament}</div>}
                          {p.notes && <div className="text-xs mt-1 text-[#6B625B]">{p.notes}</div>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {selected.last_appointment && (
                <div>
                  <h3 className="font-display text-lg font-bold mb-2">Last appointment</h3>
                  <div className="rounded-2xl bg-[#F5E6CF] p-4 text-sm">
                    <div className="font-semibold">{selected.last_appointment.service_name}</div>
                    <div className="text-xs text-[#6B625B]">
                      {new Date(selected.last_appointment.start).toLocaleString()} · {selected.last_appointment.status.replace("_"," ")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-[#E5DCCE] p-10 text-center text-[#6B625B]">
              Select a client to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
